import * as THREE from "three";
import type { RoadPaving, RoadSurface } from "./skins/kit/types";
import { KitClock } from "./skins/kit/clock";

/**
 * A road skin's own paving (kit/types.ts RoadSurface), drawn by the ground shader in place of the stock cobble on the
 * built road (groundShader.ts calls `tdPave` inside its road branch, so it costs only on road pixels). Procedural, so
 * it needs no texture of its own and no extra sampler (iOS links 16 at most): the stones' grain is the biome's rock map
 * the shader already binds, normalised by its own smallest mip so every biome's rock gives the same contrast.
 *
 * Patterns (repeats a metre = `scale`): `basket` basketweave pavers (two bricks a square, alternating), `flags`
 * irregular flagstones (a jittered Voronoi), `diamond` a 45° chequer of two stones with a stud at every crossing,
 * `hex` hexagonal setts. Every stone gets its own colour between `stone` and `stone2`, a value jitter, a rounded rim
 * (darker, and a normal tilt outward: groundGlsl.ts) and the joint colour in its joints, anti-aliased to the pixel's
 * footprint so a far road blurs to the average instead of shimmering. Lit joints pulse stone by stone on the kit clock
 * (Reduced effects freezes them with the props).
 */
export const PAVE_PATTERNS: readonly RoadPaving[] = ["basket", "flags", "diamond", "hex"];

/** The uniform values of a surface (pure: tested). Pattern id 0 = the stock cobble. */
export function packPave(s: RoadSurface | null | undefined): { pave: number[]; a: number[]; b: number[]; j: number[]; glow: number[]; wave: number[] } {
  if (!s) return { pave: [0, 1, 0, 0], a: [0, 0, 0, 0], b: [0, 0, 0, 0], j: [0, 0, 0, 0], glow: [0, 0, 0, 0], wave: [0, 0, 0, 1] };
  const id = PAVE_PATTERNS.indexOf(s.pattern) + 1;
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  const g = s.glow;
  return {
    pave: [id, Math.max(0.25, s.scale), clamp(s.jointWidth ?? 0.06, 0, 0.3) / 2, clamp(s.grain ?? 0.6, 0, 1)],
    a: [...s.stone, clamp(s.bevel ?? 0.6, 0, 1)],
    b: [...s.stone2, 0],
    j: [...s.joint, clamp(g?.scatter ?? 1, 0, 1)],
    glow: g ? [...g.colour, Math.max(0, g.base ?? 0)] : [0, 0, 0, 0],
    wave: g ? [Math.max(0, g.pulse ?? 0), g.hz ?? 0.3, g.waveK ?? 0, Math.max(1, g.sharp ?? 1)] : [0, 0, 0, 1],
  };
}

export class RoadPave {
  /** The ground material's uniforms (groundMaterials.ts binds these objects; `set` writes them in place). */
  static readonly uniforms = {
    uPave: { value: new THREE.Vector4(0, 1, 0, 0) },       // pattern id, repeats a metre, joint half-width, grain
    uPaveA: { value: new THREE.Vector4() },                // stone rgb, bevel
    uPaveB: { value: new THREE.Vector4() },                // stone2 rgb
    uPaveJ: { value: new THREE.Vector4() },                // joint rgb, the lit joints' scatter
    uPaveGlow: { value: new THREE.Vector4() },             // lit joints rgb, base
    uPaveWave: { value: new THREE.Vector4(0, 0, 0, 1) },   // pulse, hz, radians a metre, sharpness
    uPaveTime: KitClock.time,
    uPaveMotion: KitClock.motion,
  };

  /** Wears a road skin's paving, or the stock cobble for null. Takes effect at once (uniforms, no recompile). */
  static set(s: RoadSurface | null | undefined): void {
    const p = packPave(s), u = RoadPave.uniforms;
    u.uPave.value.fromArray(p.pave); u.uPaveA.value.fromArray(p.a); u.uPaveB.value.fromArray(p.b); u.uPaveJ.value.fromArray(p.j);
    u.uPaveGlow.value.fromArray(p.glow); u.uPaveWave.value.fromArray(p.wave);
  }

  static get on(): boolean { return RoadPave.uniforms.uPave.value.x > 0.5; }
}

/**
 * Declarations and `tdPave(wxz, aa, grain, out joint, out tilt, out glow)`: the paving's colour at world xz, for a pixel
 * `aa` metres across, with the stones' grain factor; `joint` 0..1 (in a joint), `tilt` the rim's outward slope (world
 * xz, for the normal chunk), `glow` the lit joints' emissive weight.
 */
export const PAVE_GLSL = `
  uniform vec4 uPave, uPaveA, uPaveB, uPaveJ, uPaveGlow, uPaveWave;
  uniform float uPaveTime, uPaveMotion;
  float tdPaveH(vec2 p) { return fract(sin(dot(p, vec2(41.31, 289.97))) * 23758.5453); }
  vec2 tdPaveH2(vec2 p) { return fract(sin(vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)))) * 43758.5453); }
  // basketweave: a square of two 0.5 x 1 bricks, laid along x on one chequer colour and along y on the other
  float tdPaveBasket(vec2 p, out vec2 n, out vec2 id) {
    vec2 c = floor(p), f = p - c;
    float chk = mod(c.x + c.y, 2.0);
    vec2 g = chk < 0.5 ? f : f.yx;
    float sub = floor(g.x * 2.0);
    vec2 u = vec2(fract(g.x * 2.0) * 0.5, g.y);
    vec2 dd = vec2(min(u.x, 0.5 - u.x), min(u.y, 1.0 - u.y));
    vec2 nn = dd.x < dd.y ? vec2(u.x < 0.25 ? -1.0 : 1.0, 0.0) : vec2(0.0, u.y < 0.5 ? -1.0 : 1.0);
    n = chk < 0.5 ? nn : nn.yx;
    id = c * 2.0 + vec2(sub, chk);
    return min(dd.x, dd.y);
  }
  // flagstones: a jittered Voronoi, the distance to the nearest edge (two 3x3 passes)
  float tdPaveFlags(vec2 p, out vec2 n, out vec2 id) {
    vec2 c = floor(p), f = p - c, mg = vec2(0.0), mr = vec2(0.0);
    float md = 8.0;
    for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
      vec2 g = vec2(float(i), float(j)), r = g + 0.15 + 0.7 * tdPaveH2(c + g) - f;
      float d = dot(r, r);
      if (d < md) { md = d; mr = r; mg = g; }
    }
    md = 8.0;
    for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
      vec2 g = mg + vec2(float(i), float(j)), r = g + 0.15 + 0.7 * tdPaveH2(c + g) - f;
      vec2 e = r - mr;
      if (dot(e, e) > 1e-5) md = min(md, dot(0.5 * (mr + r), normalize(e)));
    }
    n = normalize(-mr + 1e-5);
    id = c + mg;
    return md;
  }
  // a 45-degree chequer; a stud where four stones meet
  float tdPaveDiamond(vec2 p, out vec2 n, out vec2 id) {
    vec2 q = vec2(p.x + p.y, p.y - p.x) * 0.7071068;
    vec2 c = floor(q), f = q - c, dd = min(f, 1.0 - f);
    vec2 nn = dd.x < dd.y ? vec2(f.x < 0.5 ? -1.0 : 1.0, 0.0) : vec2(0.0, f.y < 0.5 ? -1.0 : 1.0);
    n = vec2(nn.x - nn.y, nn.x + nn.y) * 0.7071068;
    id = c;
    return min(min(dd.x, dd.y), length(f - floor(f + 0.5)) - 0.11);
  }
  // hexagonal setts (flat sides across x), apothem 0.5
  float tdPaveHex(vec2 p, out vec2 n, out vec2 id) {
    const vec2 S = vec2(1.0, 1.7320508);
    vec4 hc = floor(vec4(p, p - vec2(0.5, 1.0)) / S.xyxy) + 0.5;
    vec2 a = p - hc.xy * S, b = p - (hc.zw + 0.5) * S;
    bool first = dot(a, a) < dot(b, b);
    vec2 h = first ? a : b;
    id = first ? hc.xy : hc.zw + 0.5;
    vec2 ah = abs(h);
    float e1 = ah.x, e2 = dot(ah, vec2(0.5, 0.8660254));
    n = e1 > e2 ? vec2(sign(h.x), 0.0) : sign(h) * vec2(0.5, 0.8660254);
    return 0.5 - max(e1, e2);
  }
  vec3 tdPave(vec2 wxz, float aa, float grain, out float joint, out vec2 tilt, out float glow) {
    int pat = int(uPave.x + 0.5);
    vec2 p = wxz * uPave.y, n, id;
    float px = max(aa * uPave.y, 1e-4);
    float d = pat == 1 ? tdPaveBasket(p, n, id) : pat == 2 ? tdPaveFlags(p, n, id) : pat == 3 ? tdPaveDiamond(p, n, id) : tdPaveHex(p, n, id);
    float hw = uPave.z;
    joint = 1.0 - smoothstep(hw - px, hw + px, d);
    float h = tdPaveH(id);
    float t = pat == 3 ? mod(id.x + id.y, 2.0) * 0.8 + 0.2 * h : h;
    vec3 stone = mix(uPaveA.rgb, uPaveB.rgb, t) * (0.9 + 0.2 * tdPaveH(id + 7.31));
    stone *= mix(1.0, grain, uPave.w);
    // the rim: a band inside the joint rounds off, darker and sloping outward; it fades with distance with the joint
    float rim = (1.0 - smoothstep(hw, hw + 0.14, d)) * (1.0 - smoothstep(0.08, 0.3, px));
    stone *= 1.0 - 0.24 * uPaveA.w * rim;
    tilt = n * rim * uPaveA.w;
    glow = 0.0;
    if (uPaveGlow.w > 0.0 || uPaveWave.x > 0.0) {
      float ph = h * 6.2831853 * uPaveJ.w + dot(wxz, vec2(0.7071068)) * uPaveWave.z;
      float w = pow(0.5 + 0.5 * sin(uPaveTime * uPaveMotion * uPaveWave.y * 6.2831853 - ph), uPaveWave.w);
      glow = joint * (uPaveGlow.w + uPaveWave.x * w);
    }
    return mix(stone, uPaveJ.rgb, joint);
  }
`;

/**
 * Paves a road tile's material (the shop thumbnail's straight run of HQ path tiles along z at x = 0, skinThumb.ts):
 * the tile's up-facing texels within `half` m of x = 0 take the paving, with the surface's own uniforms so the picture
 * shows its skin whatever road is worn. The level's tile road (no ground mesh) keeps the tinted cobble.
 */
export function paveTile(m: THREE.Material, s: RoadSurface, half = 0.43): void {
  const p = packPave(s);
  const v4 = (a: number[]) => ({ value: new THREE.Vector4().fromArray(a) });
  const u = { uPave: v4(p.pave), uPaveA: v4(p.a), uPaveB: v4(p.b), uPaveJ: v4(p.j), uPaveGlow: v4(p.glow), uPaveWave: v4(p.wave), uPaveTime: KitClock.time, uPaveMotion: KitClock.motion };
  const prev = m.onBeforeCompile, key = m.customProgramCacheKey();
  m.onBeforeCompile = (sh, r) => {
    prev.call(m, sh, r);
    Object.assign(sh.uniforms, u);
    sh.vertexShader = sh.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vTdPW, vTdPN;")
      .replace("#include <worldpos_vertex>", "#include <worldpos_vertex>\n  vTdPW = (modelMatrix * vec4(transformed, 1.0)).xyz;\n  vTdPN = normalize(mat3(modelMatrix) * objectNormal);");
    sh.fragmentShader = sh.fragmentShader
      .replace("#include <common>", `#include <common>\nvarying vec3 vTdPW, vTdPN;\n${PAVE_GLSL}`)
      .replace("#include <map_fragment>", `#include <map_fragment>
  {
    float tdAa = length(fwidth(vTdPW.xz)), tdJ, tdG; vec2 tdT;
    vec3 tdC = tdPave(vTdPW.xz, tdAa, 1.0, tdJ, tdT, tdG);
    float tdOn = step(0.75, normalize(vTdPN).y) * (1.0 - smoothstep(${(half - 0.03).toFixed(3)}, ${half.toFixed(3)}, abs(vTdPW.x)));
    diffuseColor.rgb = mix(diffuseColor.rgb, tdC * (1.0 - 0.3 * clamp(length(tdT), 0.0, 1.0)), tdOn);
  }`);
  };
  m.customProgramCacheKey = () => `${key}-pave${s.pattern}`;
}
